import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Hook de salida de voz multi-motor.
 *
 * Motores disponibles:
 *  - 'google'  → Google Translate TTS (cloud, gratis, sin API key).
 *                Voz natural en español. Limita 200 chars por petición:
 *                troceamos el texto y reproducimos en cadena.
 *  - 'browser' → Web Speech API local del navegador.
 *                Calidad variable según SO/navegador. En Edge las voces
 *                "Microsoft ... Online (Natural)" suenan muy bien.
 *
 * Persistimos preferencias en localStorage.
 */

export type TtsEngine = 'google' | 'browser';
export type VoiceGender = 'any' | 'female' | 'male';

const STORAGE_KEYS = {
  enabled: 'intraeco.tts.enabled',
  engine:  'intraeco.tts.engine',
  voice:   'intraeco.tts.voice',     // nombre de la voz del navegador
  gender:  'intraeco.tts.gender',
} as const;

/**
 * Clasificación de género por nombre conocido.
 * Cubre las voces de Windows 10/11 + Edge en español de España y Latam.
 */
const FEMALE_NAMES = new Set([
  'helena', 'elvira', 'marisol', 'paloma', 'sabina', 'abril', 'dalia', 'mia',
  'esperanza', 'paulina', 'soledad', 'conchita', 'lupe', 'penelope', 'laura',
  'sara', 'ximena', 'catalina', 'rosa', 'beatriz', 'ana', 'andrea', 'larissa',
  'salome', 'yolanda', 'tamara', 'camila', 'jacqueline', 'maria', 'isabella',
  'monica', 'paola', 'patricia', 'karina',
]);
const MALE_NAMES = new Set([
  'pablo', 'jorge', 'alvaro', 'arnau', 'alonso', 'carlos', 'miguel', 'enrique',
  'raul', 'roberto', 'sergio', 'luciano', 'andres', 'dario', 'gonzalo', 'cesar',
  'gustavo', 'felipe', 'tomas', 'mateo', 'lorenzo', 'rodrigo', 'diego', 'javier',
  'antonio', 'ricardo', 'manuel', 'francisco',
]);

function detectGender(voiceName: string): VoiceGender {
  const lower = voiceName.toLowerCase();
  for (const n of FEMALE_NAMES) if (lower.includes(n)) return 'female';
  for (const n of MALE_NAMES)   if (lower.includes(n)) return 'male';
  // Heurística débil: si el nombre acaba en 'a' suele ser femenino en español.
  const match = lower.match(/\b([a-záéíóú]+)\b/g);
  const firstWord = match?.[1] ?? '';
  if (firstWord.length > 2 && firstWord.endsWith('a')) return 'female';
  return 'any';
}

const GOOGLE_TTS_BASE = 'https://translate.google.com/translate_tts';
const GOOGLE_MAX_CHARS = 190;        // margen sobre el límite real (200)

/** Heurística: voces "buenas" del navegador van primero. */
function rankBrowserVoice(v: SpeechSynthesisVoice): number {
  const n = v.name.toLowerCase();
  if (n.includes('natural'))  return 100;
  if (n.includes('neural'))   return 95;
  if (n.includes('online'))   return 90;
  if (n.includes('premium'))  return 80;
  if (n.includes('enhanced')) return 75;
  if (v.localService)         return 50;
  return 10;
}

/** Divide un texto largo en trozos ≤ maxChars, preservando palabras/frases. */
function chunkText(text: string, maxChars: number): string[] {
  const t = text.trim();
  if (t.length <= maxChars) return [t];

  // 1) Trocear por frases.
  const sentences = t.split(/(?<=[.!?¡¿])\s+/);
  const chunks: string[] = [];
  let buf = '';

  for (const s of sentences) {
    if ((buf + ' ' + s).trim().length <= maxChars) {
      buf = (buf + ' ' + s).trim();
    } else {
      if (buf) chunks.push(buf);
      // Si una frase sola excede el máximo, partir por palabras.
      if (s.length > maxChars) {
        const words = s.split(/\s+/);
        let wbuf = '';
        for (const w of words) {
          if ((wbuf + ' ' + w).trim().length <= maxChars) {
            wbuf = (wbuf + ' ' + w).trim();
          } else {
            if (wbuf) chunks.push(wbuf);
            wbuf = w;
          }
        }
        if (wbuf) chunks.push(wbuf);
        buf = '';
      } else {
        buf = s;
      }
    }
  }
  if (buf) chunks.push(buf);

  return chunks;
}

function googleTtsUrl(text: string, lang: string): string {
  const params = new URLSearchParams({
    ie:     'UTF-8',
    q:      text,
    tl:     lang,
    client: 'tw-ob',
  });
  return `${GOOGLE_TTS_BASE}?${params.toString()}`;
}

export interface UseVoiceOutputOptions {
  lang?: string;
  defaultEngine?: TtsEngine;
}

export function useVoiceOutput(opts: UseVoiceOutputOptions = {}) {
  const lang = opts.lang ?? 'es-ES';

  const [enabled, setEnabled] = useState<boolean>(() => {
    return localStorage.getItem(STORAGE_KEYS.enabled) !== 'false';
  });

  // Solo motor 'browser' visible al usuario (Google deshabilitado).
  const [engine, setEngine] = useState<TtsEngine>(() => {
    const v = localStorage.getItem(STORAGE_KEYS.engine);
    return v === 'browser' ? 'browser' : 'browser';
  });

  const [gender, setGender] = useState<VoiceGender>(() => {
    const v = localStorage.getItem(STORAGE_KEYS.gender);
    return v === 'female' || v === 'male' || v === 'any' ? v : 'any';
  });

  // Voces del navegador (para engine='browser')
  const [browserVoices, setBrowserVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [browserVoiceName, setBrowserVoiceName] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.voice) ?? '';
  });

  const [speaking, setSpeaking] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const queueRef = useRef<string[]>([]);

  // Cargar voces del navegador
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;

    const loadVoices = () => {
      const all = window.speechSynthesis.getVoices();
      const filtered = all
        .filter((v) => v.lang.toLowerCase().startsWith('es'))
        .sort((a, b) => rankBrowserVoice(b) - rankBrowserVoice(a));
      setBrowserVoices(filtered);
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  // Persistencia
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.enabled, String(enabled));
  }, [enabled]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.engine, engine);
  }, [engine]);
  useEffect(() => {
    if (browserVoiceName) localStorage.setItem(STORAGE_KEYS.voice, browserVoiceName);
  }, [browserVoiceName]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.gender, gender);
  }, [gender]);

  // Lista filtrada por género (lo que ve el dropdown)
  const filteredBrowserVoices = useMemo(() => {
    if (gender === 'any') return browserVoices;
    return browserVoices.filter((v) => {
      const g = detectGender(v.name);
      // 'any' (desconocido) lo dejamos pasar cuando se filtra, para no esconder voces que sí podrían valer.
      return g === gender || g === 'any';
    });
  }, [browserVoices, gender]);

  // Mantener una voz seleccionada coherente con el filtro de género
  useEffect(() => {
    if (browserVoices.length === 0) return;
    const current = browserVoices.find((v) => v.name === browserVoiceName);
    const currentOk = current && filteredBrowserVoices.includes(current);
    if (!currentOk && filteredBrowserVoices.length > 0) {
      setBrowserVoiceName(filteredBrowserVoices[0].name);
    }
  }, [filteredBrowserVoices, browserVoices, browserVoiceName]);

  /* ─────────────── reproducción ─────────────── */

  const cancel = useCallback(() => {
    // Browser
    if ('speechSynthesis' in window) window.speechSynthesis.cancel();
    // Google
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    queueRef.current = [];
    setSpeaking(false);
  }, []);

  const speakBrowser = useCallback(
    (text: string) => {
      if (!('speechSynthesis' in window)) return;
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = lang;

      const voice = browserVoices.find((v) => v.name === browserVoiceName);
      if (voice) utter.voice = voice;

      utter.rate = 1.0;
      utter.pitch = 1.0;
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    },
    [browserVoiceName, browserVoices, lang],
  );

  const playGoogleQueue = useCallback(() => {
    const next = queueRef.current.shift();
    if (!next) {
      setSpeaking(false);
      audioRef.current = null;
      return;
    }
    const a = new Audio(googleTtsUrl(next, lang));
    audioRef.current = a;
    a.onended = () => playGoogleQueue();
    a.onerror = () => {
      // Si Google falla, abortamos la cola y caemos al navegador con lo que quede.
      const remaining = queueRef.current.join(' ');
      queueRef.current = [];
      audioRef.current = null;
      if (remaining) speakBrowser(remaining);
      else setSpeaking(false);
    };
    a.play().catch(() => {
      // autoplay bloqueado u otro fallo → fallback navegador
      const remaining = [next, ...queueRef.current].join(' ');
      queueRef.current = [];
      audioRef.current = null;
      speakBrowser(remaining);
    });
  }, [lang, speakBrowser]);

  const speakGoogle = useCallback(
    (text: string) => {
      cancel();
      queueRef.current = chunkText(text, GOOGLE_MAX_CHARS);
      setSpeaking(true);
      playGoogleQueue();
    },
    [cancel, playGoogleQueue],
  );

  const speak = useCallback(
    (text: string) => {
      if (!enabled || !text.trim()) return;
      cancel();
      if (engine === 'google') speakGoogle(text);
      else speakBrowser(text);
    },
    [enabled, engine, cancel, speakGoogle, speakBrowser],
  );

  const toggleEnabled = useCallback(() => {
    setEnabled((v) => {
      if (v) cancel();
      return !v;
    });
  }, [cancel]);

  const supported = useMemo(
    () => 'speechSynthesis' in window || typeof Audio !== 'undefined',
    [],
  );

  return {
    // estado
    supported,
    enabled,
    engine,
    gender,
    speaking,
    browserVoices: filteredBrowserVoices,
    allBrowserVoices: browserVoices,
    browserVoiceName,
    // acciones
    speak,
    cancel,
    toggleEnabled,
    setEngine,
    setGender,
    setBrowserVoiceName,
  };
}
